import ArticleActionEnum from './enums/ArticleActionEnum';
import DesignInteractionEnum from './enums/DesignInteractionEnum';
import DesignSaveModeEnum from './enums/DesignSaveModeEnum';
import DesignSourceEnum from './enums/DesignSourceEnum';
import EventSender from './EventSender';
import InsightsApi from './InsightsApi';
import ICustomData from './types/ICustomData';
import IItems from './types/IItems';
import IModuleClient from './types/IModuleClient';
import IUserIds from './types/IUserIds';
import MetadataManager from './utilities/MetadataManager';

/**
 * Entrypoint API class for modules.
 */
export default class ModuleClient {
	private insightsApi: InsightsApi = null;
	private eventSender: EventSender;
	private moduleId: string;

	/**
	 * @param moduleMetadata The module metadata.
	 * @param insightsApi The insightsApi instance.
	 */
	constructor(moduleMetadata: IModuleClient, insightsApi: InsightsApi) {
		this.insightsApi = insightsApi;
		this.eventSender = new EventSender();
		MetadataManager.registerModule(moduleMetadata);
		this.moduleId = moduleMetadata.moduleId;
		this.eventSender.setTargetModuleId(moduleMetadata.moduleId);
		this.eventSender.sendModuleStartUpEvents();
	}

	/**
	 * Send an addToCart event, if application is enabled.
	 *
	 * @param data The included items in the cart.
	 */
	public addToCart(data: IItems): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendAddToCart(data);
		}
	}

	/**
	 * Send an addToWishList event, if application is enabled.
	 *
	 * @param data The included items in the wish list.
	 */
	public addToWishList(data: IItems): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendAddToWishList(data);
		}
	}

	/**
	 * Send a savePlanner event, if application is enabled.
	 *
	 * @param designId The design id to save, optional.
	 * @param [payload] Optional payload data.
	 * @param [mode] How this design was saved or shared.
	 */
	public saveDesign(designId?: string, payload?: {}, mode?: DesignSaveModeEnum): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendSavePlanner(designId, payload, mode);
		}
	}

	/**
	 * Send a custom event, if application is enabled.
	 *
	 * @param data The information about the custom event.
	 */
	public sendEvent(data: ICustomData): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendCustomEvent(data);
		}
	}

	/**
	 * Send an initPlanningSession event, if application is enabled.
	 *
	 * @param designId The design id that started the session.
	 * @param source The source of the planning session.
	 * @param [payload] Optional payload data.
	 * @param [spr] Optional The SPR that was used as base for the planning if any.
	 */
	public initPlanningSession(
		designId: string,
		source: DesignSourceEnum,
		payload?: {},
		spr?: string
	): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendInitPlanningSession(designId, source, payload, spr);
		}
	}

	/**
	 * Send a design interaction event, if application is enabled.
	 *
	 * @param designId The design id which was interacted with.
	 * @param interaction The interaction source.
	 * @param [payload] Optional payload data.
	 */
	public sendDesignInteractionEvent(
		designId: string,
		interaction: DesignInteractionEnum,
		payload?: {}
	): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendDesignInteractionEvent(designId, interaction, payload);
		}
	}

	/**
	 * Send a article action event, if application is enabled.
	 *
	 * @param articleId The article id.
	 * @param action The action being performed.
	 * @param isGlobal Indicate if article id is global or local.
	 * @param [payload] Optional payload data.
	 */
	public articleAction(
		articleId: string,
		action: ArticleActionEnum,
		isGlobal: boolean,
		payload?: {}
	): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendArticleAction(articleId, action, isGlobal, payload);
		}
	}

	/**
	 * Sets the A/B version for the session to facilitate A/B testing, if application is enabled.
	 *
	 * @param abVersionName Name of the ab version.
	 * @param abVersionVariation Optional ab varation.
	 */
	public setAbVersion(abVersionName: string, abVersionVariation?: string): void {
		if (this.insightsApi.isEnabled) {
			MetadataManager.updateModule(
				{
					abVersionName,
					abVersionVariation
				},
				this.moduleId
			);
			this.eventSender.sendSetAbVersion();
		}
	}

	/**
	 * Send a flow step event, if the component is enabled.
	 *
	 * @param flowName Name of the flow.
	 * @param stepName Name of the step in the flow.
	 * @param stepLevel Level of step in the flow.
	 */
	public sendUserFlowStep(
		flowName: string,
		stepName: string,
		stepLevel: number,
		payload?: {}
	): void {
		if (this.insightsApi.isEnabled) {
			this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
		}
	}

	/**
	 * Send a flow step event, if the component is enabled.
	 * Asynchronous version of this function.
	 *
	 * @param flowName Name of the flow.
	 * @param stepName Name of the step in the flow.
	 * @param stepLevel Level of step in the flow.
	 */
	public async sendUserFlowStepAsync(
		flowName: string,
		stepName: string,
		stepLevel: number,
		payload?: {}
	): Promise<void> {
		if (this.insightsApi.isEnabled) {
			await this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
		}
	}

	/**
	 * Get user ids.
	 *
	 * @returns The user ids (sessionId, planningId, longTermId and ipexId).
	 */
	public getIds(): IUserIds | undefined {
		if (this.insightsApi.isEnabled) {
			return this.insightsApi.getIds();
		}
	}
}
